/**
 * Pure selector functions for deriving data from canonical state
 * All selectors accept state as first argument and are deterministic (no side effects)
 * 
 * State shape expected:
 * {
 *   assignmentsById: { [id]: assignment },
 *   eventsById: { [id]: event },
 *   documentsById: { [id]: document },
 *   coursesById: { [id]: course },
 *   dataVersion: number
 * }
 */

import {
  normEndeavorId,
  normEventId,
  normAssignmentId,
  normDueDate,
  normStartDate,
  isInRange,
  DEBUG_PERF
} from './dataUtils';

// ============================================================================
// Course Progress Selector
// ============================================================================

/**
 * Get progress statistics for a specific course
 * @param {Object} state - Canonical state object
 * @param {string|number} endeavorId - Course/endeavor ID
 * @returns {{ total: number, completed: number, percent: number }} Progress stats
 */
export function selectCourseProgress(state, endeavorId) {
  if (DEBUG_PERF) console.time('selectCourseProgress');
  
  if (!state?.assignmentsById || !endeavorId) {
    if (DEBUG_PERF) console.timeEnd('selectCourseProgress');
    return { total: 0, completed: 0, percent: 0 };
  }
  
  const assignments = Object.values(state.assignmentsById).filter(a => {
    if (!a || a.isDeleted) return false;
    const aEndeavorId = normEndeavorId(a);
    return aEndeavorId === endeavorId;
  });
  
  const total = assignments.length;
  
  if (total === 0) {
    if (DEBUG_PERF) console.timeEnd('selectCourseProgress');
    return { total: 0, completed: 0, percent: 0 };
  }
  
  const completed = assignments.filter(isCompletedAssignment).length;
  
  const rawPercent = (completed / total) * 100;
  const percent = Math.min(100, Math.max(0, Number.isFinite(rawPercent) ? rawPercent : 0));
  
  if (DEBUG_PERF) console.timeEnd('selectCourseProgress');
  return { total, completed, percent };
}

// ============================================================================
// Assignments Selector
// ============================================================================

/**
 * Get all assignments for a specific course
 * @param {Object} state - Canonical state object
 * @param {string|number} endeavorId - Course/endeavor ID
 * @returns {Array} Filtered assignments array
 */
export function selectAssignmentsByCourse(state, endeavorId) {
  if (DEBUG_PERF) console.time('selectAssignmentsByCourse');
  
  if (!state?.assignmentsById) {
    if (DEBUG_PERF) console.timeEnd('selectAssignmentsByCourse');
    return [];
  }
  
  const assignments = Object.values(state.assignmentsById).filter(a => {
    if (!a || a.isDeleted) return false;
    if (!endeavorId) return true; // No filter, return all non-deleted
    const aEndeavorId = normEndeavorId(a);
    return aEndeavorId === endeavorId;
  });
  
  if (DEBUG_PERF) console.timeEnd('selectAssignmentsByCourse');
  return assignments;
}

// ============================================================================
// Events Selector
// ============================================================================

/**
 * Get all events for a specific course
 * @param {Object} state - Canonical state object
 * @param {string|number} endeavorId - Course/endeavor ID (optional)
 * @returns {Array} Filtered events array
 */
export function selectEventsByCourse(state, endeavorId) {
  if (DEBUG_PERF) console.time('selectEventsByCourse');
  
  if (!state?.eventsById) {
    if (DEBUG_PERF) console.timeEnd('selectEventsByCourse');
    return [];
  }
  
  const events = Object.values(state.eventsById).filter(e => {
    if (!e || e.isDeleted) return false;
    if (!endeavorId) return true; // No filter, return all non-deleted
    const eEndeavorId = normEndeavorId(e);
    return eEndeavorId === endeavorId;
  });
  
  if (DEBUG_PERF) console.timeEnd('selectEventsByCourse');
  return events;
}

// ============================================================================
// Documents Selector
// ============================================================================

/**
 * Get all documents for a specific course
 * @param {Object} state - Canonical state object
 * @param {string|number} endeavorId - Course/endeavor ID (optional)
 * @returns {Array} Filtered documents array
 */
export function selectDocumentsByCourse(state, endeavorId) {
  if (DEBUG_PERF) console.time('selectDocumentsByCourse');
  
  if (!state?.documentsById) {
    if (DEBUG_PERF) console.timeEnd('selectDocumentsByCourse');
    return [];
  }
  
  const documents = Object.values(state.documentsById).filter(d => {
    if (!d || d.isDeleted) return false;
    if (!endeavorId) return true; // No filter, return all non-deleted
    const dEndeavorId = normEndeavorId(d);
    return dEndeavorId === endeavorId;
  });
  
  if (DEBUG_PERF) console.timeEnd('selectDocumentsByCourse');
  return documents;
}

// ============================================================================
// Timeline Items Selector
// ============================================================================

/**
 * Get timeline items (assignments + events) for a date range
 * @param {Object} state - Canonical state object
 * @param {Object} options - Filter options
 * @param {Date|string} options.start - Start date
 * @param {Date|string} options.end - End date
 * @param {string|number} [options.courseId] - Optional course filter
 * @returns {Array} Combined and sorted timeline items
 */
export function selectTimelineItems(state, { start, end, courseId }) {
  if (DEBUG_PERF) console.time('selectTimelineItems');
  
  if (!state?.assignmentsById || !state?.eventsById) {
    if (DEBUG_PERF) console.timeEnd('selectTimelineItems');
    return [];
  }
  
  const items = [];
  const seenIds = new Set();
  
  // Add assignments with due dates in range
  Object.values(state.assignmentsById).forEach(assignment => {
    if (!assignment || assignment.isDeleted) return;
    
    const id = normAssignmentId(assignment);
    if (!id || seenIds.has(`assignment-${id}`)) return;
    
    const dueDate = normDueDate(assignment);
    if (!dueDate) return;
    
    // Filter by course if specified
    if (courseId) {
      const assignmentCourseId = normEndeavorId(assignment);
      if (assignmentCourseId !== courseId) return;
    }
    
    // Filter by date range if specified
    if (start && end && !isInRange(dueDate, start, end)) return;
    
    seenIds.add(`assignment-${id}`);
    items.push({
      ...assignment,
      type: 'assignment',
      date: dueDate,
      _normalizedDate: new Date(dueDate)
    });
  });
  
  // Add events with start dates in range
  Object.values(state.eventsById).forEach(event => {
    if (!event || event.isDeleted) return;
    
    const id = normEventId(event);
    if (!id || seenIds.has(`event-${id}`)) return;
    
    const startDate = normStartDate(event);
    if (!startDate) return;
    
    // Filter by course if specified
    if (courseId) {
      const eventCourseId = normEndeavorId(event);
      if (eventCourseId !== courseId) return;
    }
    
    // Filter by date range if specified
    if (start && end && !isInRange(startDate, start, end)) return;
    
    seenIds.add(`event-${id}`);
    items.push({
      ...event,
      type: 'event',
      date: startDate,
      _normalizedDate: new Date(startDate)
    });
  });
  
  // Sort by date ascending
  items.sort((a, b) => {
    const dateA = a._normalizedDate?.getTime() || 0;
    const dateB = b._normalizedDate?.getTime() || 0;
    return dateA - dateB;
  });
  
  if (DEBUG_PERF) console.timeEnd('selectTimelineItems');
  return items;
}

// ============================================================================
// Calendar Items Selector
// ============================================================================

/**
 * Get calendar items (assignments + events) for a specific month
 * @param {Object} state - Canonical state object
 * @param {Object} options - Filter options
 * @param {number} options.year - Year
 * @param {number} options.month - Month (0-11)
 * @param {string|number} [options.courseId] - Optional course filter
 * @returns {Array} Combined calendar items
 */
export function selectCalendarItems(state, { year, month, courseId }) {
  if (DEBUG_PERF) console.time('selectCalendarItems');
  
  if (!year || month == null) {
    if (DEBUG_PERF) console.timeEnd('selectCalendarItems');
    return [];
  }
  
  // Calculate month range
  const start = new Date(year, month, 1);
  const end = new Date(year, month + 1, 0, 23, 59, 59, 999);
  
  // Reuse timeline selector with date range
  const items = selectTimelineItems(state, { start, end, courseId });
  
  if (DEBUG_PERF) console.timeEnd('selectCalendarItems');
  return items;
}

// ============================================================================
// Utility: Get All Courses
// ============================================================================

/**
 * Get all courses
 * @param {Object} state - Canonical state object
 * @returns {Array} All non-deleted courses
 */
export function selectAllCourses(state) {
  if (!state?.coursesById) return [];
  
  return Object.values(state.coursesById).filter(c => c && !c.isDeleted);
}

// ============================================================================
// Utility: Get Upcoming Assignments
// ============================================================================

/**
 * Get upcoming incomplete assignments (next 7 days by default)
 * @param {Object} state - Canonical state object
 * @param {number} [days=7] - Number of days to look ahead
 * @returns {Array} Upcoming incomplete assignments
 */
export function selectUpcomingAssignments(state, days = 7) {
  if (!state?.assignmentsById) return [];
  
  const now = new Date();
  const end = new Date(now.getTime() + days * 24 * 60 * 60 * 1000);
  
  return Object.values(state.assignmentsById)
    .filter(a => {
      if (!a || a.isDeleted) return false;
      
      // Only incomplete assignments
      const isComplete = a.completed || 
                        a.status === 'Completed' || 
                        a.submissionStatus === 'Completed';
      if (isComplete) return false;
      
      const dueDate = normDueDate(a);
      if (!dueDate) return false;
      
      const dueDateObj = new Date(dueDate);
      return dueDateObj >= now && dueDateObj <= end;
    })
    .sort((a, b) => {
      const dateA = new Date(normDueDate(a)).getTime();
      const dateB = new Date(normDueDate(b)).getTime();
      return dateA - dateB;
    });
}

// ============================================================================
// Utility: Get Overdue Assignments
// ============================================================================

/**
 * Get overdue incomplete assignments
 * @param {Object} state - Canonical state object
 * @returns {Array} Overdue incomplete assignments
 */
export function selectOverdueAssignments(state) {
  if (!state?.assignmentsById) return [];
  
  const now = new Date();
  
  return Object.values(state.assignmentsById)
    .filter(a => {
      if (!a || a.isDeleted) return false;
      
      // Only incomplete assignments
      if (isCompletedAssignment(a)) return false;
      
      const dueDate = normDueDate(a);
      if (!dueDate) return false;
      
      return new Date(dueDate) < now;
    })
    .sort((a, b) => {
      const dateA = new Date(normDueDate(a)).getTime();
      const dateB = new Date(normDueDate(b)).getTime();
      return dateA - dateB;
    });
}

// ============================================================================
// Shared Helpers
// ============================================================================

/**
 * Shared completion check logic - used by both course progress and trophy progress
 * @param {Object} assignment - Assignment object
 * @returns {boolean} True if assignment is completed
 */
export function isCompletedAssignment(assignment) {
  if (!assignment) return false;
  return assignment.completed ||
         assignment.status === 'completed' ||
         assignment.status === 'Completed' ||
         assignment.completedAt != null ||
         assignment.completed_at != null ||
         assignment.submissionStatus === 'Completed' ||
         assignment.completionStatus === 'Completed';
}

/**
 * Shared completion check for events
 * @param {Object} event - Event object  
 * @returns {boolean} True if event is completed/past
 */
export function isCompletedEvent(event) {
  if (!event) return false;
  
  // Events are "completed" if they are in the past
  const startDate = normStartDate(event);
  if (!startDate) return false;
  
  return new Date(startDate) < new Date();
}

// ============================================================================
// Trophy Progress Selector
// ============================================================================

/**
 * Get global progress counts for trophy/achievement system
 * @param {Object} state - Canonical state object
 * @returns {Object} Global progress statistics
 */
export function selectTrophyProgress(state) {
  if (DEBUG_PERF) console.time('selectTrophyProgress');
  
  if (!state) {
    if (DEBUG_PERF) console.timeEnd('selectTrophyProgress');
    return {
      assignmentsCompleted: 0,
      assignmentsTotal: 0,
      eventsCompleted: 0,
      eventsTotal: 0,
      documentsUploaded: 0,
      coursesTotal: 0,
    };
  }
  
  // Assignments: use shared completion logic
  const allAssignments = state.assignmentsById ? 
    Object.values(state.assignmentsById).filter(a => !a?.isDeleted) : [];
  const assignmentsTotal = allAssignments.length;
  const assignmentsCompleted = allAssignments.filter(isCompletedAssignment).length;
  
  // Events: past events are "completed"
  const allEvents = state.eventsById ? 
    Object.values(state.eventsById).filter(e => !e?.isDeleted) : [];
  const eventsTotal = allEvents.length;
  const eventsCompleted = allEvents.filter(isCompletedEvent).length;
  
  // Debug logging for events
  if (eventsTotal > 0) {
    const sampleEvent = allEvents[0];
    console.log('[selectTrophyProgress] Events debug:', {
      total: eventsTotal,
      completed: eventsCompleted,
      sampleEvent: sampleEvent,
      sampleStartDate: sampleEvent ? normStartDate(sampleEvent) : null,
      sampleIsCompleted: sampleEvent ? isCompletedEvent(sampleEvent) : null,
      sampleEventKeys: sampleEvent ? Object.keys(sampleEvent) : [],
      sampleEventDateFields: sampleEvent ? {
        startAt: sampleEvent.startAt,
        startDate: sampleEvent.startDate,
        start_at: sampleEvent.start_at,
        start_date: sampleEvent.start_date,
        eventDate: sampleEvent.eventDate,
        event_date: sampleEvent.event_date,
        startDatetime: sampleEvent.startDatetime,
        date: sampleEvent.date
      } : null
    });
  }
  
  // Documents: all non-deleted documents count as "uploaded"
  const documentsUploaded = state.documentsById ? 
    Object.values(state.documentsById).filter(d => !d?.isDeleted).length : 0;
  
  // Debug logging for documents
  console.log('[selectTrophyProgress] Documents debug:', {
    documentsById: state.documentsById,
    documentsCount: state.documentsById ? Object.keys(state.documentsById).length : 0,
    documentsUploaded,
    sampleDoc: state.documentsById ? Object.values(state.documentsById)[0] : null
  });
  
  // Courses
  const coursesTotal = state.coursesById ? 
    Object.keys(state.coursesById).length : 0;
  
  if (DEBUG_PERF) console.timeEnd('selectTrophyProgress');
  
  return {
    assignmentsCompleted,
    assignmentsTotal,
    eventsCompleted,
    eventsTotal,
    documentsUploaded,
    coursesTotal,
  };
}
